import { useMemo } from 'react';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SalesOrderMetadata } from './sales-order-metadata';
import { SalesOrderLineItems } from './sales-order-line-items';
import { ChargesTable } from '@/components/common/charges-table';
import { useChargesForEntity } from '@/api/rpc/hooks/finance/useCharges';
import { SystemEntity } from '@erp/shared-types';
import type { GetSalesOrderResponse } from '@erp/rpc';
import { T, Var } from 'gt-react';

type SalesOrderWithDetails = NonNullable<GetSalesOrderResponse['salesOrder']>;

export interface SalesOrderDetailsProps {
  salesOrder: SalesOrderWithDetails;
  lineItems?: GetSalesOrderResponse['lineItems'];
}

export function SalesOrderDetails({
  salesOrder,
  lineItems,
}: SalesOrderDetailsProps) {
  // Fetch charges for the sales order
  const { charges: allCharges } = useChargesForEntity({
    entityType: SystemEntity.AR_SALES_ORDER,
    entityId: salesOrder.id,
    includeLineCharges: false, // Only header charges
  });

  // Filter to only header charges (no docLineId)
  const headerCharges = useMemo(
    () => allCharges.filter((charge) => !charge.docLineId),
    [allCharges],
  );

  const hasLineItems = lineItems && lineItems.length > 0;
  const hasCharges = headerCharges.length > 0;

  return (
    <div className="h-full flex flex-col space-y-4 overflow-y-auto px-3">
      <div className="shrink-0">
        <SalesOrderMetadata salesOrder={salesOrder} />
      </div>

      {/* Tabs for Line Items and Charges */}
      {(hasLineItems || hasCharges) && (
        <>
          <Separator />
          <Tabs
            defaultValue={hasLineItems ? 'line-items' : 'charges'}
            className="w-full"
          >
            <TabsList className="w-full">
              {hasLineItems && (
                <TabsTrigger value="line-items">
                  <T context="AR sales order detail tab with line item count">
                    Line Items (<Var>{lineItems.length}</Var>)
                  </T>
                </TabsTrigger>
              )}
              {hasCharges && (
                <TabsTrigger value="charges">
                  <T context="AR sales order detail tab with charge count">
                    Charges (<Var>{headerCharges.length}</Var>)
                  </T>
                </TabsTrigger>
              )}
            </TabsList>
            {hasLineItems && (
              <TabsContent value="line-items" className="mt-0">
                <SalesOrderLineItems
                  lineItems={lineItems}
                  currency={salesOrder.currencyCode}
                  shipment={salesOrder.shipment}
                />
              </TabsContent>
            )}
            {hasCharges && (
              <TabsContent value="charges" className="mt-0">
                <div className="px-3">
                  <ChargesTable
                    charges={headerCharges}
                    currency={salesOrder.currencyCode ?? 'USD'}
                  />
                </div>
              </TabsContent>
            )}
          </Tabs>
        </>
      )}
    </div>
  );
}
